import React from "react";
import { Link } from "react-router-dom";
import { ButtonLink } from "./ButtonLink";
import { SiteImage } from "./SiteImage";

export function TextBlock({ text, className = "" }) {
  return (
    <div className={`text-block ${className}`.trim()}>
      {text.split("\n\n").map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </div>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`card ${className}`.trim()}>{children}</div>;
}

export function SectionHeading({ eyebrow, title, copy }) {
  return (
    <div className="section-heading">
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <h2>{title}</h2>
      {copy ? <TextBlock text={copy} /> : null}
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  copy,
  actions,
  image,
  badge,
  tags = [],
  metaTitle,
  metaItems = [],
  variant = "default",
  className = "",
}) {
  return (
    <section className={`page-hero page-hero-${variant} ${className}`.trim()} data-testid="page-hero">
      <div className="container page-hero-grid">
        <div className="page-hero-content">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          {badge ? <span className="hero-badge">{badge}</span> : null}
          <h1>{title}</h1>
          {copy ? <TextBlock text={copy} className="hero-copy" /> : null}
          {tags.length > 0 ? (
            <div className="hero-tags" aria-label={`${title} highlights`}>
              {tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          ) : null}
          {actions ? <div className="hero-actions">{actions}</div> : null}
        </div>
        {image ? (
          <div className="page-hero-media">
            <SiteImage {...image} priority />
            {metaTitle || metaItems.length > 0 ? (
              <div className="page-hero-card">
                {metaTitle ? <p className="page-hero-card-title">{metaTitle}</p> : null}
                {metaItems.length > 0 ? (
                  <ul>
                    {metaItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function HomeHero({ eyebrow, title, copy, image, facts, actions }) {
  return (
    <section className="home-hero" data-testid="home-hero">
      <SiteImage {...image} className="home-hero-photo" priority fallbackLabel="Redland Golf & Country Club" />
      <div className="home-hero-shade" aria-hidden="true" />
      <div className="container home-hero-inner">
        <div className="home-hero-copy">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          <h1>{title}</h1>
          <p>{copy}</p>
          <div className="hero-actions">{actions}</div>
        </div>
        <div className="home-hero-panel" aria-label="Course highlights">
          <span className="public-badge">Open to the Public</span>
          <div className="home-hero-facts">
            {facts.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function QuickActionCard({ label, href, detail, index }) {
  const content = (
    <>
      <span className="quick-action-number">{String(index + 1).padStart(2, "0")}</span>
      <h3>{label}</h3>
      <p>{detail}</p>
      <span className="quick-action-arrow" aria-hidden="true">
        -&gt;
      </span>
    </>
  );

  if (href.startsWith("/")) {
    return (
      <Link className="quick-action-card" to={href} data-testid="quick-action-card">
        {content}
      </Link>
    );
  }

  return (
    <a className="quick-action-card" href={href} data-testid="quick-action-card">
      {content}
    </a>
  );
}

export function PhotoPanel({ image, label, className = "" }) {
  return (
    <figure className={`photo-panel ${className}`.trim()}>
      <SiteImage {...image} />
      {label ? <figcaption>{label}</figcaption> : null}
    </figure>
  );
}

export function EditorialCard({ eyebrow, title, copy, image, href, linkLabel }) {
  return (
    <Card className="editorial-card">
      {image ? <SiteImage {...image} /> : null}
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h3>{title}</h3>
        {copy ? <p>{copy}</p> : null}
        {href && linkLabel ? (
          <ButtonLink href={href} variant="secondary">
            {linkLabel}
          </ButtonLink>
        ) : null}
      </div>
    </Card>
  );
}

export function FeatureGrid({ items }) {
  return (
    <div className="feature-grid">
      {items.map((item) => (
        <Card className="feature-card" key={item}>
          <span className="feature-dot" aria-hidden="true" />
          <p>{item}</p>
        </Card>
      ))}
    </div>
  );
}

export function RateTable({ title, rows }) {
  return (
    <Card className="rate-card">
      <h3>{title}</h3>
      <div className="responsive-table" role="region" aria-label={`${title} pricing`}>
        <table>
          <thead>
            <tr>
              <th>Time or Category</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.label}-${row.price}`}>
                <td>{row.label}</td>
                <td>{row.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function PriceList({ title, rows }) {
  return (
    <Card>
      <h3>{title}</h3>
      <div className="price-list">
        {rows.map((row) => (
          <div key={`${row.label}-${row.price}`}>
            <span>{row.label}</span>
            <strong>{row.price}</strong>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function HoursTable({ rows }) {
  return (
    <div className="responsive-table hours-table" role="region" aria-label="Winter hours">
      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Breakfast</th>
            <th>Kitchen</th>
            <th>Sand Trap</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.day}>
              <td>{row.day}</td>
              <td>{row.breakfast ?? "Not listed"}</td>
              <td>{row.kitchen}</td>
              <td>{row.sandTrap}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MembershipCard({ name, description, monthly, yearly }) {
  return (
    <Card className="membership-card">
      <h3>{name}</h3>
      {description ? <p>{description}</p> : null}
      <div className="membership-prices">
        <span>{monthly}</span>
        <strong>{yearly}</strong>
      </div>
    </Card>
  );
}

export function EventCard({ title, date, description }) {
  return (
    <Card className="event-card">
      <p className="event-label">{date ?? "Activity"}</p>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
    </Card>
  );
}

export function PolicyAlert({ title, children, tone = "gold" }) {
  return (
    <div className={`policy-alert policy-${tone}`}>
      <h3>{title}</h3>
      <div>{children}</div>
    </div>
  );
}

export function Accordion({ title, children, open = false }) {
  return (
    <details className="accordion" open={open}>
      <summary>{title}</summary>
      <div>{children}</div>
    </details>
  );
}

export function Breadcrumbs({ current }) {
  return (
    <nav className="breadcrumbs container" aria-label="Breadcrumb">
      <Link to="/">Home</Link>
      <span aria-hidden="true">/</span>
      <span>{current}</span>
    </nav>
  );
}

export function EmptyState({ message }) {
  return (
    <div className="empty-state" role="status">
      <p>{message}</p>
    </div>
  );
}

export function ContactInfoCard({ title, children }) {
  return (
    <Card className="contact-info-card">
      <h3>{title}</h3>
      <div>{children}</div>
    </Card>
  );
}

export function FloatingAction({ href }) {
  return (
    <a className="floating-action" href={href} aria-label="Call Pro Shop for tee times" data-testid="floating-call-action">
      Call Pro Shop
    </a>
  );
}
